/* eslint-disable @typescript-eslint/ban-ts-comment */
import React from 'react';
import i18n from 'src/localization/i18n';
import { Brand } from 'src/types';
import {
  CREATE_NEW_MESSAGE_FROM_FAQ,
  DOCUMENT_CENTER_FROM_FAQ,
  PAPERLESS_NOTIFICATIONS_PATH,
  PAYMENT_OPTIONS_PATH,
  PROFILE_PATH,
} from 'src/constants/routePaths';
import {
  FAQComponentItem,
  FAQComponentItemList,
  FAQComponentItemParagraph,
  FAQComponentItemParagraphWithLinks,
  FAQComponentItemParagraphWithTitle,
  FAQComponentItemTextWithLinks,
  FAQComponentProps,
  FAQComponentSection,
  FAQComponentWithLinksProps,
  FAQComponentItemSubTitle,
  LinkType,
  resourceKey,
} from './FAQComponent';
import * as rk from '../../../localization/resourceKeys';
// @ts-ignore
import thirdPartyAuthorizationAudi from '../../../assets/pdfs/faq/ThirdPartyAuthorization-Audi.pdf';
// @ts-ignore
import thirdPartyAuthorizationVW from '../../../assets/pdfs/faq/ThirdPartyAuthorization-VW.pdf';

const LeaseFAQ: React.FC<Omit<FAQComponentProps, 'accountType'>> = ({ brand }) => {
  const styleBrand = brand === Brand.AUDI ? Brand.AUDI : Brand.VW;
  const messageCenterUrl = CREATE_NEW_MESSAGE_FROM_FAQ;
  const documentCenterUrl = DOCUMENT_CENTER_FROM_FAQ;
  const paperlessNotificationUrl = PAPERLESS_NOTIFICATIONS_PATH;
  const accountSettingsUrl = PROFILE_PATH;
  const showDDCFAQ = process.env.REACT_APP_DDC === 'true';
  const locateADealerAudi = 'https://www.audiusa.com/us/web/en/shopping-tools/dealer-search.html';
  const locateADealerVW = 'http://www.vw.com/find-a-dealer';
  const vehicleRegistrationUrl =
    'https://www.dmv.ca.gov/portal/vehicle-registration/titles/title-transfers-and-changes/notice-of-transfer-and-release-of-liability-nrl/';
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
  return (
    <main className='c-faq u-pv u-ph'>
      <h4 className='header'>{i18n.t(rk.FAQ_TITLE) as string}</h4>
      <div>
        {/* Most common questions section */}
        <FAQComponentSection className='u-mb' value={i18n.t(rk.FAQ_LEASE_MOST_COMMON_QUESTIONS_SECTION)}>
          {/** FAQ 1 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_GENERAL_QUESTION2)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH2', styleBrand)} />
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH3_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH3_CONTENT', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
          </FAQComponentItem>
          {/** FAQ 2 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION1_ANSWER1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 3 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION2)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_NEW_QUESTION2_ANSWER1', styleBrand)} />
            <FAQComponentItemList
              items={[
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM1),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM2),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                      value: messageCenterUrl as string,
                      type: LinkType.NavLink,
                    },
                  ],
                } as FAQComponentWithLinksProps,
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM3),
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM4),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                      value: documentCenterUrl as string,
                      type: LinkType.NavLink,
                    },
                  ],
                } as FAQComponentWithLinksProps,
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM6),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM7),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM7_LINK1),
                      value: paperlessNotificationUrl as string,
                      type: LinkType.NavLink,
                    },
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                      type: LinkType.Nav,
                    },
                  ],
                } as FAQComponentWithLinksProps,
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 4 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION3_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION_NOTE_TITLE)}>
              {i18n.t(rk.FAQ_LEASE_NEW_QUESTION3_ANSWER1_PARAGRAPH2_CONTENT)}
            </FAQComponentItemParagraphWithTitle>
          </FAQComponentItem>
          {/* FAQ 5 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH1_LINK1),
                  type: LinkType.Nav,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH2)} />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION_NOTE_TITLE)}>
              {i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH3_CONTENT)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH4)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH5)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH6)} />
          </FAQComponentItem>
          {/* FAQ 6 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION5)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION5_ANSWER1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 7 and 8 */}
          <FAQComponentItem
            value={showDDCFAQ ? i18n.t(rk.FAQ_LEASE_NEW_QUESTION6) : i18n.t(rk.FAQ_PAYMENTOPTIONS_QUESTION5)}
          >
            <FAQComponentItemParagraphWithLinks
              value={
                showDDCFAQ
                  ? i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH1_US)
                  : i18n.t(resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH1_US_DDC_DISABLED', styleBrand))
              }
              links={
                showDDCFAQ
                  ? [
                      {
                        key: i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LINK_US),
                        type: LinkType.NavLink,
                        value: PAYMENT_OPTIONS_PATH,
                      },
                    ]
                  : []
              }
            />
            {showDDCFAQ && (
              <>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH2_US)}</p>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH3_US)}</p>
                <ul>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM1_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM2_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM3_US)}</li>
                </ul>
                <p>{resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH4_US', styleBrand)}</p>
                <ul>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM1_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM2_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM4_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM5_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM6_US)}</li>
                </ul>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH5_US)}</p>
                <p>{resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH6_US', styleBrand)}</p>
              </>
            )}
          </FAQComponentItem>
          {/* FAQ 9 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_MOVING_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_MOVING_ANSWER1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_MOVING_ANSWER1_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 10 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ACCOUNT_SETTINGS_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 11 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH2)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH3', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 12  */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_TURNIN_QUESTION1)}>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH1_TITLE', styleBrand)}
            >
              <FAQComponentItemParagraphWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH1_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_WEAR_AND_USE_GUIDELINES_LINK),
                    value: i18n.t(
                      brand === Brand.AUDI
                        ? 'https://www.audiusa.com/us/web/en/shopping-tools/financial-services-hub/lease-or-finance/turn-in-process.html'
                        : 'https://www.vw.com/en/financial-services/lease-transition-options/turn-in-process.html'
                    ),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_CONTENT', styleBrand)}
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_CONTENT2', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH3_TITLE', styleBrand)}
            >
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH3_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_TURNIN_ANSWER1_LINK3),
                    value: i18n.t(brand === Brand.AUDI ? locateADealerAudi : locateADealerVW),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemList
              items={[
                {
                  value: resourceKey('FAQ_LEASE_TURNIN_ANSWER1_LISTITEM1', styleBrand),
                  links: [
                    {
                      key: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_AUDI
                          : rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_VW
                      ),
                      value: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_AUDI
                          : rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_VW
                      ),
                      type: LinkType.Email,
                    },
                  ],
                },
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_LISTITEM2', styleBrand),
                {
                  value: resourceKey('FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_PARAGRAPH6', styleBrand),
                  links: [
                    {
                      key: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_AUDI
                          : rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_VW
                      ),
                      value: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_AUDI
                          : rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_VW
                      ),
                      type: LinkType.Email,
                    },
                  ],
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_TITLE)}>
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT', styleBrand)}
                links={[
                  {
                    key:
                      brand === Brand.AUDI
                        ? i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT_LINK_AUDI)
                        : i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT_LINK_VW),
                    value: i18n.t(brand === Brand.AUDI ? locateADealerAudi : locateADealerVW),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemList
              items={[
                {
                  value: i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT2),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                      value: messageCenterUrl,
                      type: LinkType.NavLink,
                    },
                  ],
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_TITLE)}>
              <FAQComponentItemTextWithLinks
                value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_CONTENT)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                    value: documentCenterUrl as string,
                    type: LinkType.NavLink,
                  },
                ]}
              />
              <FAQComponentItemTextWithLinks
                value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_CONTENT2)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                    value: documentCenterUrl as string,
                    type: LinkType.NavLink,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH6_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH6_CONTENT', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH7', styleBrand)} />
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH8_TITLE', styleBrand)}
            >
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH8_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_TURNIN_ANSWER1_LINK4),
                    value: vehicleRegistrationUrl,
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH9', styleBrand)} />
            <FAQComponentItemList
              items={[
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM1', styleBrand),
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM2', styleBrand),
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM3', styleBrand),
              ]}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Titles section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_TITLES)}>
          {/* FAQ 13 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_TITLES_QUESTION1)}>
            {process.env.REACT_APP_TITLE_RELEASE_FLAG === 'true' && (
              <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_ANSWER1_BCO_PARAGRAPH1)} />
            )}
            {process.env.REACT_APP_TITLE_RELEASE_FLAG === 'true' && (
              <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_ANSWER1_BCO_PARAGRAPH2)} bold />
            )}
            {process.env.REACT_APP_TITLE_RELEASE_FLAG === 'false' && (
              <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_ANSWER1_PARAGRAPH1)} />
            )}
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_TITLES_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 14 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_TITLES_QUESTION8)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_TITLES_ANSWER8_PARAGRAPH1_WITHLINK', styleBrand)} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_REGULAR_MAIL_ADDRESS_PARAGRAPH1)} bold />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_REGULAR_MAIL_ADDRESS_PARAGRAPH2)} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_OVERNIGHT_MAIL_ADDRESS_PARAGRAPH1)} bold />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_TITLES_OVERNIGHT_MAIL_ADDRESS_PARAGRAPH2)} />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Payments section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_PAYMENTS_SECTION)}>
          {/* FAQ 15 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION9)}>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION9_ANSWER1_PARAGRAPH1)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION9_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                  value: documentCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 16 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION10)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION10_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION10_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 17 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION11)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION11_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 18 and 19 */}
          <FAQComponentItem
            value={showDDCFAQ ? i18n.t(rk.FAQ_LEASE_NEW_QUESTION6) : i18n.t(rk.FAQ_PAYMENTOPTIONS_QUESTION5)}
          >
            <FAQComponentItemParagraphWithLinks
              value={
                showDDCFAQ
                  ? i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH1_US)
                  : i18n.t(resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH1_US_DDC_DISABLED', styleBrand))
              }
              links={
                showDDCFAQ
                  ? [
                      {
                        key: i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LINK_US),
                        type: LinkType.NavLink,
                        value: PAYMENT_OPTIONS_PATH,
                      },
                    ]
                  : []
              }
            />
            {showDDCFAQ && (
              <>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH2_US)}</p>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH3_US)}</p>
                <ul>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM1_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM2_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST1_ITEM3_US)}</li>
                </ul>
                <p>{resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH4_US', styleBrand)}</p>
                <ul>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM1_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM2_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM4_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM5_US)}</li>
                  <li>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_LIST2_ITEM6_US) as string}</li>
                </ul>
                <p>{i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH5_US)}</p>
                <p>{resourceKey('FAQ_PAYMENTOPTIONS_ANSWER5_PARAGRAPH6_US', styleBrand)}</p>
              </>
            )}
          </FAQComponentItem>
          {/* FAQ 20 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_PAYMENTOPTIONS_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_PAYMENTOPTIONS_ANSWER1_LINK1),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH2', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH3', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH4', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 21 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION1)}>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION1_ANSWER1_PARAGRAPH1)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION1_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: capitalize(i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY)),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION1_ANSWER1_PARAGRAPH3)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION1_ANSWER1_PARAGRAPH4', styleBrand)}
            />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH3', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH4', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 22 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION2_ANSWER1_PARAGRAPH1)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH3', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER1_PARAGRAPH5', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 23 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION1_ANSWER1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 24 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION3_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION_NOTE_TITLE)}>
              {i18n.t(rk.FAQ_LEASE_NEW_QUESTION3_ANSWER1_PARAGRAPH2_CONTENT)}
            </FAQComponentItemParagraphWithTitle>
          </FAQComponentItem>
          {/* FAQ 25 */}
          <FAQComponentItem value={resourceKey('FAQ_PAYMENTOPTIONS_QUESTION4', styleBrand)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER4_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 26 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION3)}>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION3_ANSWER1_PARAGRAPH1)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION3_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_PAYMENT_OPTIONS_QUESTION3_ANSWER1_PARAGRAPH2_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Account management section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_SECTION)}>
          {/* FAQ 27 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION1_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                  value: documentCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 28 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ACCOUNT_SETTINGS_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION7_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 29 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION2)}>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1)} />
            <FAQComponentItemList
              items={[
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM1),
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM2_OPTION2),
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM3),
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM4),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                      value: documentCenterUrl as string,
                      type: LinkType.NavLink,
                    },
                  ],
                } as FAQComponentWithLinksProps,
                i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM6),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM7),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM7_LINK1),
                      value: paperlessNotificationUrl as string,
                      type: LinkType.NavLink,
                    },
                    {
                      key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                      type: LinkType.Nav,
                    },
                  ],
                } as FAQComponentWithLinksProps,
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 30 */}
          <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION2', styleBrand)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION2_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 31 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_PAPERLESSBILLING_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION3_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ACCOUNT_SETTINGS_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 32 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH1_LINK1),
                  type: LinkType.Nav,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION_NOTE_TITLE)}>
              {i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH3_CONTENT2)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH4)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH5)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION4_ANSWER1_PARAGRAPH6)} />
          </FAQComponentItem>
          {/* FAQ 33 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION5)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION5_ANSWER1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ENROLL_IN_AUTOPAY),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 34 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_PAPERLESSBILLING_QUESTION4)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_PAPERLESSBILLING_ANSWER4_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK1),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK1),
                  type: LinkType.Email,
                },
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK2),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK2),
                  type: LinkType.Email,
                },
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK3),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK3),
                  type: LinkType.Email,
                },
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK5),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK5),
                  type: LinkType.Email,
                },
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK6),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK6),
                  type: LinkType.Email,
                },
                {
                  key: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK7),
                  value: i18n.t(rk.FAQ_PAPERLESSBILLING_ANSWER4_LINK7),
                  type: LinkType.Email,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION4_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_ACCOUNT_SETTINGS_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 35 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION5)}>
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION5_ANSWER1_PARAGRAPH1)}
            />
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION5_ANSWER1_PARAGRAPH2)}
            />
          </FAQComponentItem>
          {/* FAQ 36 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_THIRDPARTYAUTHORIZATION_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_THIRDPARTYAUTHORIZATION_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_THIRDPARTYAUTHORIZATION_ANSWER1_LINK1),
                  value: brand === Brand.AUDI ? thirdPartyAuthorizationAudi : thirdPartyAuthorizationVW,
                  type: LinkType.NewPage,
                },
                {
                  key: i18n.t(rk.FAQ_THIRDPARTYAUTHORIZATION_ANSWER1_LINK2),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 37 */}
          <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION6', styleBrand)}>
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION6_ANSWER1_PARAGRAPH1', styleBrand)}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* General lease inquiries section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_SECTION)}>
          {/* FAQ 38 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION7)}>
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION7_ANSWER1_PARAGRAPH1)}
            />
          </FAQComponentItem>
          {/* FAQ 39 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_GENERAL_QUESTION3)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_GENERAL_ANSWER3_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_ACCOUNT_MANAGEMENT_QUESTION8_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 40 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION1_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                  value: documentCenterUrl as string,
                  type: LinkType.NavLink,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 41 */}
          <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION2', styleBrand)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION2_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1),
                  value: documentCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 42 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_GENERAL_QUESTION2)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH2', styleBrand)} />
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH3_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_GENERAL_ANSWER2_PARAGRAPH3_CONTENT', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
          </FAQComponentItem>
          {/* FAQ 43 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION4)}>
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION4_ANSWER1_PARAGRAPH1)}
            />
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION4_ANSWER1_PARAGRAPH2', styleBrand)}
            />
          </FAQComponentItem>
          {/* FAQ 44 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_PURCHASING_QUESTION1)}>
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION3_ANSWER1_PARAGRAPH1)}
            />
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_GENERALLEASEINQUIRIES_QUESTION3_ANSWER1_PARAGRAPH2)}
            />
            <FAQComponentItemList
              items={[
                resourceKey('FAQ_LEASE_PURCHASING_ANSWER1_PARAGRAPH3_CONTENT', styleBrand),
                resourceKey('FAQ_LEASE_PURCHASING_ANSWER1_PARAGRAPH4_CONTENT', styleBrand),
              ]}
            />
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_PURCHASING_ANSWER1_PARAGRAPH5_CONTENT', styleBrand)}
            />
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_PURCHASING_ANSWER1_PARAGRAPH6', styleBrand)} />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Lease-end section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_SECTION)}>
          {/* FAQ 45 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_TURNIN_QUESTION1)}>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH1_TITLE', styleBrand)}
            >
              <FAQComponentItemParagraphWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH1_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_WEAR_AND_USE_GUIDELINES_LINK),
                    value: i18n.t(
                      brand === Brand.AUDI
                        ? 'https://www.audiusa.com/us/web/en/shopping-tools/financial-services-hub/lease-or-finance/turn-in-process.html'
                        : 'https://www.vw.com/en/financial-services/lease-transition-options/turn-in-process.html'
                    ),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_CONTENT', styleBrand)}
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH2_CONTENT2', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH3_TITLE', styleBrand)}
            >
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH3_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_TURNIN_ANSWER1_LINK3),
                    value: i18n.t(brand === Brand.AUDI ? locateADealerAudi : locateADealerVW),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemList
              items={[
                {
                  value: resourceKey('FAQ_LEASE_TURNIN_ANSWER1_LISTITEM1', styleBrand),
                  links: [
                    {
                      key: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_AUDI
                          : rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_VW
                      ),
                      value: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_AUDI
                          : rk.FAQ_LEASE_TURNIN_ANSWER1_LINK1_VW
                      ),
                      type: LinkType.Email,
                    },
                  ],
                },
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_LISTITEM2', styleBrand),
                {
                  value: resourceKey('FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_PARAGRAPH6', styleBrand),
                  links: [
                    {
                      key: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_AUDI
                          : rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_VW
                      ),
                      value: i18n.t(
                        brand === Brand.AUDI
                          ? rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_AUDI
                          : rk.FAQ_LEASE_VEHICLEINSPECTIONS_ANSWER1_LINK3_VW
                      ),
                      type: LinkType.Email,
                    },
                  ],
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_TITLE)}>
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT', styleBrand)}
                links={[
                  {
                    key:
                      brand === Brand.AUDI
                        ? i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT_LINK_AUDI)
                        : i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT_LINK_VW),
                    value: i18n.t(brand === Brand.AUDI ? locateADealerAudi : locateADealerVW),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemList
              items={[
                {
                  value: i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH4_CONTENT2),
                  links: [
                    {
                      key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                      value: messageCenterUrl,
                      type: LinkType.NavLink,
                    },
                  ],
                },
              ]}
            />
            <FAQComponentItemParagraphWithTitle value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_TITLE)}>
              <FAQComponentItemTextWithLinks
                value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_CONTENT)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1) as string,
                    value: documentCenterUrl as string,
                    type: LinkType.NavLink,
                  },
                ]}
              />
              <FAQComponentItemTextWithLinks
                value={i18n.t(rk.FAQ_LEASE_TURNIN_NEW_ANSWER1_PARAGRAPH5_CONTENT2)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION2_ANSWER1_LISTITEM5_LINK1) as string,
                    value: documentCenterUrl as string,
                    type: LinkType.NavLink,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH6_TITLE', styleBrand)}
            >
              {resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH6_CONTENT', styleBrand)}
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH7', styleBrand)} />
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH8_TITLE', styleBrand)}
            >
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH8_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_TURNIN_ANSWER1_LINK4),
                    value: vehicleRegistrationUrl,
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH9', styleBrand)} />
            <FAQComponentItemList
              items={[
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM1', styleBrand),
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM2', styleBrand),
                resourceKey('FAQ_LEASE_TURNIN_ANSWER1_PARAGRAPH10_LISTITEM3', styleBrand),
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 46 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_QUESTION1)}>
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_PARAGRAPH1', styleBrand)}
            />
            <FAQComponentItemList
              items={[
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM1', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM2', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM3', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM4', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM5', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM6', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM7', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM8', styleBrand),
                resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LISTITEM9', styleBrand),
              ]}
            />
            <FAQComponentItemParagraphWithTitle
              value={resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_PARAGRAPH2_TITLE', styleBrand)}
            >
              <FAQComponentItemTextWithLinks
                value={resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_PARAGRAPH2_CONTENT', styleBrand)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_LINK1),
                    value: i18n.t(
                      brand === Brand.AUDI
                        ? 'https://www.audiusa.com/us/web/en/shopping-tools/financial-services-hub/lease-or-finance/turn-in-process.html'
                        : 'https://www.vw.com/en/financial-services/lease-transition-options/turn-in-process.html'
                    ),
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItemParagraphWithTitle>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER1_PARAGRAPH3', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 47 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_PURCHASING_QUESTION3)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_PURCHASING_ANSWER3_PARAGRAPH1', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 48 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER3_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(
                    brand === Brand.AUDI
                      ? rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER3_LINK1_AUDI
                      : rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER3_LINK1_VW
                  ),
                  value: i18n.t(
                    brand === Brand.AUDI
                      ? rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER3_LINK1_AUDI
                      : rk.FAQ_LEASE_TURNINSETTLEMENTINVOICE_ANSWER3_LINK1_VW
                  ),
                  type: LinkType.Email,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 49 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION1_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 50 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_GENERAL_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_GENERAL_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 51 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_EARLYTERMINATION_QUESTION1)}>
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_EARLYTERMINATION_ANSWER1_PARAGRAPH1', styleBrand)}
            />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_EARLYTERMINATION_ANSWER1_PARAGRAPH2', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 52 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION2)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_NEW_LEASEEND_QUESTION2_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: resourceKey('FAQ_LEASE_NEW_LEASEEND_QUESTION2_ANSWER1_PARAGRAPH1_LINK1', styleBrand),
                  value:
                    brand === Brand.AUDI
                      ? 'https://www.audiusa.com/us/web/en/shopping-tools/financial-services-hub/lease-or-finance/lease-transition-options.html'
                      : 'https://www.vw.com/en/financial-services/lease-transition-options.html',
                  type: LinkType.NewPage,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION2_ANSWER1_PARAGRAPH2)} />
          </FAQComponentItem>
          {/* FAQ 53 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_LEASEEND_QUESTION3_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: resourceKey('FAQ_LEASE_NEW_LEASEEND_QUESTION3_ANSWER1_PARAGRAPH1_LINK1', styleBrand),
                  value: 'https://www.dmvusa.com/',
                  type: LinkType.NewPage,
                },
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Payoff section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_PAYOFF_SECTION)}>
          {/* FAQ 54 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_HOMEPAGE_LINK),
                  type: LinkType.Nav,
                },
              ]}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH2)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_LEASE_NEW_QUESTION8_ANSWER1_PARAGRAPH3', styleBrand)} />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Special situations section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_SECTION)}>
          {/* FAQ 55 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_ACCIDENT_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_ACCIDENT_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
            <FAQComponentItemParagraph value={resourceKey('FAQ_ACCIDENT_ANSWER1_PARAGRAPH2', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_ACCIDENT_ANSWER1_PARAGRAPH3', styleBrand)} />
          </FAQComponentItem>
          {/* FA 56 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_MOVING_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_MOVING_ANSWER1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_MOVING_ANSWER1_LINK),
                  value: accountSettingsUrl,
                  type: LinkType.NavLink,
                },
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 57 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_SCRA_QUESTION1)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_SCRA_ANSWER1_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemList
              items={[
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM1', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM2', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM3', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM4', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM5', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM6', styleBrand),
                resourceKey('FAQ_SCRA_ANSWER1_LISTITEM7', styleBrand),
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_SCRA_ANSWER1_PARAGRAPH2', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_SCRA_ANSWER1_LINK1),
                  value: 'https://legalassistance.law.af.mil/',
                  type: LinkType.NewPage,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_SCRA_ANSWER1_PARAGRAPH3', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_ANSWER1_PARAGRAPH1)}
            />
            <ul>
              <li>{i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM1)}</li>
              <li>
                <FAQComponentItemTextWithLinks
                  value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM2)}
                  links={[
                    {
                      key: 'https://scra.dmdc.osd.mil',
                      value: 'https://scra.dmdc.osd.mil',
                      type: LinkType.NewPage,
                    },
                  ]}
                />
              </li>
              <li>
                {i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM3)}
                <ul>
                  <li>{i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM3_EXTRA_1)}</li>
                  <li>{i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM3_EXTRA_2)}</li>
                </ul>
              </li>
            </ul>
            {/* <FAQComponentItemList
              items={[
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM1),
                {
                  value: i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM2) as string,
                  links: [
                    {
                      key: 'https://scra.dmdc.osd.mil',
                      value: 'https://scra.dmdc.osd.mil',
                      type: LinkType.NewPage,
                    },
                  ],
                },

                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH1_LISTITEM3),
              ]}
            />
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM3_EXTRA)}
            /> */}
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_ANSWER1_PARAGRAPH2)}
            />
            <FAQComponentItemList
              items={[
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM1),
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM2),
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM3),
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM4),
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM5),
                i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION1_PARAGRAPH2_LISTITEM6),
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 58 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_TRANSFERMYACCOUNT_QUESTION1)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_TRANSFERMYACCOUNT_ANSWER1_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_TRANSFERMYACCOUNT_ANSWER1_PARAGRAPH2', styleBrand)} />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_TRANSFERMYACCOUNT_ANSWER1_PARAGRAPH3', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 59 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION2)}>
            <FAQComponentItemParagraph value={resourceKey('FAQ_CONSUMERCREDIT_ANSWER1_PARAGRAPH1', styleBrand)} />
            <FAQComponentItemParagraph value={resourceKey('FAQ_CONSUMERCREDIT_ANSWER1_PARAGRAPH2', styleBrand)} />
          </FAQComponentItem>
          {/* FAQ 60 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3)}>
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_PARAGRAPH1)}
            />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_LISTITEM1)} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_LISTITEM2)} />
            <FAQComponentItemList items={[i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_LISTITEM3)]} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_LISTITEM4)} />
            <FAQComponentItemParagraph
              value={i18n.t(rk.FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_PARAGRAPH2)}
            />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION3_ANSWER1_PARAGRAPH3', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 61 */}
          <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION4', styleBrand)}>
            <FAQComponentItemParagraph
              value={resourceKey('FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION4_ANSWER1_PARAGRAPH1', styleBrand)}
            />
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_NEW_SPECIALSITUATIONS_QUESTION4_ANSWER1_PARAGRAPH2', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK),
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Disaster relief section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_SECTION)}>
          {/* FAQ 62 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION1)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION1_ANSWER1_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK) as string,
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 63 */}
          <FAQComponentItem value={resourceKey('FAQ_PAYMENTOPTIONS_QUESTION4', styleBrand)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey('FAQ_PAYMENTOPTIONS_ANSWER4_PARAGRAPH1', styleBrand)}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_QUESTION_MESSAGECENTER_LINK) as string,
                  value: messageCenterUrl as string,
                  type: LinkType.NavLink,
                },
              ]}
            />
          </FAQComponentItem>
          {/* FAQ 64  */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION2)}>
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION2_ANSWER1_PARAGRAPH1)} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION2_ANSWER1_PARAGRAPH2)} />
            <FAQComponentItemParagraph value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION2_ANSWER1_PARAGRAPH3)} />
          </FAQComponentItem>
          {/* FAQ 65  */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION3_ANSWER1_PARAGRAPH1)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
            <FAQComponentItemParagraphWithLinks
              value={i18n.t(rk.FAQ_LEASE_NEW_DISASTER_RELIEF_QUESTION3_ANSWER1_PARAGRAPH2)}
              links={[
                {
                  key: i18n.t(rk.FAQ_TITLES_MESSAGES_LINK),
                  value: messageCenterUrl,
                  type: LinkType.NavLink,
                },
              ]}
            />
            <FAQComponentItemParagraph value={resourceKey('FAQ_ACCIDENT_ANSWER1_PARAGRAPH3', styleBrand)} />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Insurance and vehicle protection section */}
        <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_SECTION)}>
          {/* FAQ 66 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION1)}>
            <FAQComponentItemParagraph
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION1_ANSWER1_PARAGRAPH1',
                styleBrand
              )}
            />
            <FAQComponentItemParagraph
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION1_ANSWER1_PARAGRAPH2',
                styleBrand
              )}
            />
          </FAQComponentItem>
          {/* FAQ 67 */}
          <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2', styleBrand)}>
            <FAQComponentItemParagraph
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2_ANSWER1_PARAGRAPH1',
                styleBrand
              )}
            />
            <FAQComponentItemParagraph
              small
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2_ANSWER1_PARAGRAPH2',
                styleBrand
              )}
            />
            <FAQComponentItemParagraph
              small
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2_ANSWER1_PARAGRAPH3',
                styleBrand
              )}
            />
            <FAQComponentItemParagraph
              small
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2_ANSWER1_PARAGRAPH4',
                styleBrand
              )}
            />
            <FAQComponentItemParagraph
              small
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION2_ANSWER1_PARAGRAPH5',
                styleBrand
              )}
            />
          </FAQComponentItem>
          {/* FAQ 68 */}
          <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION3)}>
            <FAQComponentItemParagraphWithLinks
              value={resourceKey(
                'FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION3_ANSWER1_PARAGRAPH1',
                styleBrand
              )}
              links={[
                {
                  key: i18n.t(rk.FAQ_LEASE_NEW_INSURANCEANDVEHICLEPROTECTION_QUESTION3_ANSWER1_PARAGRAPH1_LINK1),
                  value: i18n.t(
                    brand === Brand.AUDI
                      ? 'https://www.audiusa.com/us/web/en/shopping-tools/dealer-search.html'
                      : 'https://www.vw.com/en/dealer-search.html?---=%7B%22dealer-search_featureappsection%22%3A%22%2F%22%7D'
                  ),
                  type: LinkType.NewPage,
                },
              ]}
            />
          </FAQComponentItem>
        </FAQComponentSection>
        {/* Wells fargo section */}
        {process.env.REACT_APP_SHOW_WF_QUESTIONS === 'true' && (
          <FAQComponentSection value={i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_SECTION)} dataTestId='wf-faq'>
            {/* FAQ 69 */}
            <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION1', styleBrand)}>
              <FAQComponentItemParagraph
                value={resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION1_ANSWER1_PARAGRAPH1', styleBrand)}
              />
            </FAQComponentItem>
            {/* FAQ 70 */}
            <FAQComponentItem value={resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION2', styleBrand)}>
              <FAQComponentItemSubTitle value={i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH1)} />
              <FAQComponentItemList
                items={[
                  resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH1_LISTITEM1', styleBrand),
                  {
                    value: resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH1_LISTITEM2', styleBrand),
                    links: [
                      {
                        key: i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH2_LINK1),
                        value:
                          'https://connect.secure.wellsfargo.com/auth/login/present?origin=cob&LOB=CONS&_gl=1*sfe2rf*_ga*MTQ1NjY2MjQyOC4xNzI5NjI0Njk3*_ga_7JXJJ2JF12*MTczMjIwOTY5OC45LjAuMTczMjIwOTY5OC42MC4wLjA.',
                        type: LinkType.NewPage,
                      },
                      {
                        key: i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH2_LINK2),
                        value: 'https://www.wellsfargo.com/mobile-online-banking/apps/',
                        type: LinkType.NewPage,
                      },
                    ],
                  },
                ]}
              />
              <FAQComponentItemSubTitle value={i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH2)} />
              <FAQComponentItemList
                items={[
                  i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH2_LISTITEM1),
                  resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION2_ANSWER1_PARAGRAPH2_LISTITEM2', styleBrand),
                ]}
              />
            </FAQComponentItem>
            {/* FAQ 71 */}
            <FAQComponentItem value={i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3)}>
              <FAQComponentItemParagraph
                value={resourceKey('FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH1', styleBrand)}
              />
              <FAQComponentItemParagraphWithLinks
                value={i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH2)}
                links={[
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH2_LINK1),
                    value:
                      'https://connect.secure.wellsfargo.com/auth/login/present?origin=cob&LOB=CONS&_gl=1*sfe2rf*_ga*MTQ1NjY2MjQyOC4xNzI5NjI0Njk3*_ga_7JXJJ2JF12*MTczMjIwOTY5OC45LjAuMTczMjIwOTY5OC42MC4wLjA.',
                    type: LinkType.NewPage,
                  },
                  {
                    key: i18n.t(rk.FAQ_LEASE_NEW_WELLSFARGO_QUESTION3_ANSWER1_PARAGRAPH2_LINK2),
                    value: 'https://www.wellsfargo.com/mobile-online-banking/apps/',
                    type: LinkType.NewPage,
                  },
                ]}
              />
            </FAQComponentItem>
          </FAQComponentSection>
        )}
      </div>
    </main>
  );
};

export default LeaseFAQ;
